document.addEventListener("DOMContentLoaded", () => {
  const cartApiUrl = "/cart.js"; // Shopify's cart API endpoint

  // Move updateCartState declaration to the top
  const updateCartState = () => {
    const currentState = history.state || {};
    history.replaceState(
      { 
        ...currentState, 
        cartUpdateTime: Date.now() 
      }, 
      ''
    );
  };

  const updateButtonState = (button, { disabled, text, addClasses = [], removeClasses = [], clickHandler }) => {
    if (!button) return;

    button.disabled = disabled;
    if (disabled) {
      button.setAttribute("data-disabled", "disabled");
    } else {
      button.removeAttribute("disabled");
      button.removeAttribute("data-disabled"); 
    }

    // Update classes
    addClasses.forEach((className) => button.classList.add(className));
    removeClasses.forEach((className) => button.classList.remove(className));

    button.innerHTML = `<span>${text}</span>`;
    if (clickHandler === null || clickHandler === undefined) {
      button.onclick = null;
      button.removeAttribute('onclick');
    } else {
      button.onclick = clickHandler;
    }
  };

  const setAddedToCartButton = (button) => {
    updateButtonState(button, {
      disabled: true,
      text: "Added to Cart",
      addClasses: ["added-to-cart"],
      removeClasses: ["loading", "go-to-cart"],
    });
  };

  const setGoToCartButton = (button) => {
    updateButtonState(button, {
      disabled: false,
      text: "Added to Cart",
      addClasses: ["go-to-cart"],
      removeClasses: ["added-to-cart", "loading"],
      clickHandler: () => {
        updateButtonState(button, {
          disabled: true,
          text: "Redirecting...",
          addClasses: ["go-to-cart"],
        });
        setTimeout(() => {
          window.location.href = "/cart";
        }, 500);
      },
    });
  };

  const resetAddToCartButton = (button) => {
    if (!button) return;

    updateButtonState(button, {
      disabled: false,
      text: "Add to Cart",
      removeClasses: ["added-to-cart", "go-to-cart", "loading"],
      clickHandler: null, 
    });
    
    button.removeAttribute("data-disabled");
    button.onclick = null;
  };

  let updateCartButtonsTimeout = null;
  const updateCartButtons = async (source = 'unknown') => {
    
    if (updateCartButtonsTimeout) {
      clearTimeout(updateCartButtonsTimeout);
      updateCartButtonsTimeout = null;
    }

    updateCartButtonsTimeout = setTimeout(async () => {
      try {
        const response = await fetch(cartApiUrl, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
          cache: 'no-cache'
        });
        if (!response.ok) throw new Error("Failed to fetch cart contents");

        const cart = await response.json();
        const cartItems = new Set(cart.items.map((item) => item.variant_id));

        document.querySelectorAll('.cart-count-bubble').forEach(bubble => {
          const countSpan = bubble.querySelector('span[aria-hidden="true"]');
          const ariaSpan = bubble.querySelector('.visually-hidden');
          if (countSpan) countSpan.innerHTML = cart.item_count;
          if (ariaSpan) ariaSpan.innerHTML = `${cart.item_count} item${cart.item_count !== 1 ? 's' : ''}`;
        });

        let buttonsUpdated = 0;
        document.querySelectorAll("form[action='/cart/add']").forEach((form) => {
          const variantIdInput = form.querySelector("input[name='id']");
          const submitButton = form.querySelector("button[type='submit']");
          
          if (variantIdInput && submitButton) {
            const variantId = parseInt(variantIdInput.value, 10);
            
            if (cartItems.has(variantId)) {
              setGoToCartButton(submitButton);
              buttonsUpdated++;
            } else {
              resetAddToCartButton(submitButton);
              buttonsUpdated++;
            }
          }
        });
      } catch (error) {
        console.error('[Cart Button] Error updating cart buttons:', error);
      } finally {
        updateCartButtonsTimeout = null;
      }
    }, 150);
  };

  // Add-to-cart submission is handled solely by Dawn's product-form.js to avoid
  // duplicate /cart/add requests, which split bundle line items and trigger the
  // quantity enforcer to remove them. This file only manages button state; it
  // reacts to the cartUpdate pub/sub event that product-form.js publishes (see
  // the PUB_SUB_EVENTS.cartUpdate subscription below) to flip the button to its
  // "Go to Cart" state after an add. Owned products already hide and disable the
  // form via checkPurchasedProducts(), so no add can fire for them.

  const checkPurchasedProducts = () => {
    if (!window.cartValidationCustomerData || !window.cartValidationCustomerData.isLoggedIn) {
      document.querySelectorAll('.product-form__buttons.checking-purchase').forEach(buttons => {
        buttons.classList.add('loaded');
      });
      return;
    }

    const productForms = document.querySelectorAll("form[action='/cart/add']");
    productForms.forEach((form) => {
      const productForm = form.closest('.product-form');
      if (!productForm) return;
      
      const buttonsContainer = form.querySelector('.product-form__buttons');
      
      const alreadyPurchasedMessage = productForm?.parentElement?.querySelector('.already-purchased-message');
      if (alreadyPurchasedMessage) {
        if (buttonsContainer) {
          buttonsContainer.classList.remove('checking-purchase', 'loaded');
          buttonsContainer.style.display = 'none';
        }
        return;
      }

      let productId = productForm?.dataset?.productId;
      
      if (!productId) {
        const productInfo = productForm.closest('[data-product-id]');
        productId = productInfo?.dataset?.productId;
      }
      
      if (!productId) {
        const productIdInput = form.querySelector('input[name="product-id"]');
        productId = productIdInput?.value;
      }

      if (!productId) {
        if (buttonsContainer) {
          buttonsContainer.classList.add('loaded');
        }
        return;
      }

      const orders = window.cartValidationCustomerData.orders || [];
      let productPurchased = false;

      for (const order of orders) {
        for (const lineItem of order.lineItems || []) {
          if (parseInt(lineItem.productId) === parseInt(productId)) {
            productPurchased = true;
            break;
          }
        }
        if (productPurchased) break;
      }

      if (productPurchased && productForm && !productForm.parentElement.querySelector('.already-purchased-message')) {
        const goToMediaDiv = document.createElement('div');
        goToMediaDiv.className = 'already-purchased-message';
        goToMediaDiv.innerHTML = '<a href="/account#my-media" class="button button--full-width primary-button shape-down">Go to My Media</a>';
        
        productForm.parentElement.insertBefore(goToMediaDiv, productForm);
        productForm.style.display = 'none';
        
        form.style.display = 'none';
        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.disabled = true;
        }
        
        if (buttonsContainer) {
          buttonsContainer.classList.remove('checking-purchase', 'loaded');
          buttonsContainer.style.display = 'none';
        }
      } else {
        if (buttonsContainer) {
          buttonsContainer.classList.add('loaded');
        }
      }
    });
  };

  // Initial button update on page load
  updateCartButtons();
  updateCartState();
  
  const runPurchasedProductsCheck = () => {
    if (window.cartValidationCustomerData && window.cartValidationCustomerData.isLoggedIn) {
      checkPurchasedProducts();
      return true;
    } else if (window.cartValidationCustomerData && !window.cartValidationCustomerData.isLoggedIn) {
      document.querySelectorAll('.product-form__buttons.checking-purchase').forEach(buttons => {
        buttons.classList.add('loaded');
      });
      return true;
    }
    return false;
  };
  
  if (!runPurchasedProductsCheck()) {
    const checkInterval = setInterval(() => {
      if (runPurchasedProductsCheck()) {
        clearInterval(checkInterval);
      }
    }, 50); 
    setTimeout(() => {
      clearInterval(checkInterval);
      document.querySelectorAll('.product-form__buttons.checking-purchase').forEach(buttons => {
        buttons.classList.add('loaded');
      });
    }, 2000);
  }
  
  document.addEventListener('cartValidationDataReady', () => {
    checkPurchasedProducts();
  });
  
  // Debounce the full-page purchased-products scan. The observer watches the
  // whole document subtree, so without this it would re-run checkPurchasedProducts()
  // (a querySelectorAll over every add-to-cart form) on every single DOM mutation,
  // inflating interaction processing time / INP. (HELP-16061)
  let purchasedCheckTimeout = null;
  const observer = new MutationObserver(() => {
    if (!(window.cartValidationCustomerData && window.cartValidationCustomerData.isLoggedIn)) return;
    if (purchasedCheckTimeout) clearTimeout(purchasedCheckTimeout);
    purchasedCheckTimeout = setTimeout(() => {
      purchasedCheckTimeout = null;
      checkPurchasedProducts();
    }, 200);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Add navigation state handlers
  window.addEventListener('popstate', () => {
    updateCartButtons();
  });

  window.addEventListener('pageshow', (event) => {
    // Update on back/forward navigation including bfcache restore
    if (event.persisted) {
      updateCartButtons();
    }
  });

  document.addEventListener('cart:updated', async (event) => {
    if (event.detail?.source === 'add-to-cart-button') {
      return;
    }
    
    await updateCartButtons('cart:updated event');
  });

  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      const delay = event.source === 'cart-items' ? 400 : 200;
      setTimeout(async () => {
        await updateCartButtons('PUB_SUB_EVENTS.cartUpdate');
      }, delay);
    });
  } else {
    let lastCartItemCount = null;
    setInterval(async () => {
      try {
        const response = await fetch(cartApiUrl, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
          cache: 'no-cache'
        });
        if (response.ok) {
          const cart = await response.json();
          if (lastCartItemCount !== null && cart.item_count !== lastCartItemCount) {
            await updateCartButtons('polling fallback');
          }
          lastCartItemCount = cart.item_count;
        }
      } catch (error) {
      }
    }, 1000);
  }
  
  const setupCartRemoveListeners = () => {
    document.addEventListener('click', async (event) => {
      const removeButton = event.target.closest('cart-remove-button');
      if (removeButton) {
        setTimeout(async () => {
          await updateCartButtons('remove button click');
        }, 500);
        
        setTimeout(async () => {
          await updateCartButtons('remove button click delayed');
        }, 1000);
        return;
      }
      
      const rebuyRemoveButton = event.target.closest('[data-rebuy-action="remove"], .rebuy-cart__item-remove, [class*="remove"]');
      if (rebuyRemoveButton && rebuyRemoveButton.closest('.rebuy-cart__flyout')) {
        setTimeout(async () => {
          await updateCartButtons('Rebuy remove button click');
        }, 500);
        
        setTimeout(async () => {
          await updateCartButtons('Rebuy remove button click delayed');
        }, 1200);
        return;
      }
      
      const clickedElement = event.target;
      if (clickedElement.closest('.rebuy-cart__flyout')) {
        const isRemoveAction = clickedElement.textContent?.toLowerCase().includes('remove') ||
                              clickedElement.textContent?.toLowerCase().includes('delete') ||
                              clickedElement.getAttribute('aria-label')?.toLowerCase().includes('remove') ||
                              clickedElement.classList.contains('remove') ||
                              clickedElement.closest('[class*="remove"]');
        
        if (isRemoveAction) {
          setTimeout(async () => {
            await updateCartButtons('Rebuy remove action');
          }, 600);
        }
      }
    });
  };
  
  setupCartRemoveListeners();
  
  const setupCartDrawerItemsWatcher = () => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.tagName === 'CART-DRAWER-ITEMS' || node.querySelector('cart-drawer-items')) {
              setTimeout(async () => {
                await updateCartButtons('cart-drawer-items added');
              }, 500);
            }
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };
  
  setupCartDrawerItemsWatcher();

  const observeRebuyCartDrawer = () => {
    const rebuyCartFlyout = document.querySelector('.rebuy-cart__flyout');
    const rebuyCartItems = document.querySelector('[data-rebuy-component="cart-items"]');
    
    if (rebuyCartFlyout) {
      if (rebuyCartItems) {
        const rebuyObserver = new MutationObserver((mutations) => {
          let hasRelevantChange = false;
          
          mutations.forEach((mutation) => {
            if (mutation.removedNodes.length > 0 || mutation.addedNodes.length > 0) {
              hasRelevantChange = true;
            }
            
            if (mutation.addedNodes.length > 0) {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1) {
                  const emptyCart = node.querySelector?.('.rebuy-cart__flyout-empty-cart') || 
                                   (node.classList?.contains('rebuy-cart__flyout-empty-cart'));
                  if (emptyCart) {
                    hasRelevantChange = true;
                  }
                }
              });
            }
          });
          
          if (hasRelevantChange) {
            setTimeout(async () => {
              await updateCartButtons('Rebuy cart items observer');
            }, 400);
          }
        });
        
        rebuyObserver.observe(rebuyCartItems, {
          childList: true,
          subtree: true,
          attributes: false
        });
      }
      
      const flyoutObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const isVisible = !rebuyCartFlyout.classList.contains('is-hidden') && 
                            rebuyCartFlyout.offsetParent !== null;
            if (!isVisible) {
              setTimeout(async () => {
                await updateCartButtons('Rebuy cart drawer closed');
              }, 200);
            }
          }
        });
      });
      
      flyoutObserver.observe(rebuyCartFlyout, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }
  };
  
  setTimeout(observeRebuyCartDrawer, 1000);
  
  setInterval(() => {
    const rebuyCart = document.querySelector('.rebuy-cart__flyout');
    if (rebuyCart && !rebuyCart.dataset.observed) {
      rebuyCart.dataset.observed = 'true';
      observeRebuyCartDrawer();
    }
  }, 2000);
  
  if (window.Rebuy) {
    document.addEventListener('rebuy:cart:update', async () => {
      setTimeout(async () => {
        await updateCartButtons('Rebuy cart:update event');
      }, 300);
    });
    
    document.addEventListener('rebuy:cart:change', async () => {
      setTimeout(async () => {
        await updateCartButtons('Rebuy cart:change event');
      }, 300);
    });
  }
  
  const observeCartDrawerContent = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      return;
    }
    
    const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
    const cartItemsContainer = cartDrawer.querySelector('#CartDrawer-CartItems');
    
    if (cartDrawerItems) {
      const itemsObserver = new MutationObserver((mutations) => {
        setTimeout(async () => {
          await updateCartButtons('cart-drawer-items observer');
        }, 300);
      });
      
      itemsObserver.observe(cartDrawerItems, {
        childList: true,
        subtree: true,
        attributes: true
      });
    }
    
    if (cartItemsContainer) {
      const contentObserver = new MutationObserver((mutations) => {
        setTimeout(async () => {
          await updateCartButtons('cart drawer content observer');
        }, 300);
      });
      
      contentObserver.observe(cartItemsContainer, {
        childList: true,
        subtree: true
      });
    }
  };
  
  setTimeout(observeCartDrawerContent, 1000);

  const setupCartDrawerObserver = () => {
    const rebuyCartFlyout = document.querySelector('.rebuy-cart__flyout');
    if (rebuyCartFlyout) {
      let wasVisible = rebuyCartFlyout.offsetParent !== null && 
                      !rebuyCartFlyout.classList.contains('is-hidden');
      
      const rebuyObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes') {
            const isVisible = rebuyCartFlyout.offsetParent !== null && 
                            !rebuyCartFlyout.classList.contains('is-hidden');
            
            if (isVisible !== wasVisible) {
              wasVisible = isVisible;
              
              if (!isVisible) {
                setTimeout(async () => {
                  await updateCartButtons('Rebuy cart drawer closed');
                }, 200);
              } else {
                setTimeout(async () => {
                  await updateCartButtons('Rebuy cart drawer opened');
                }, 300);
              }
            }
          }
        });
      });

      rebuyObserver.observe(rebuyCartFlyout, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });

      document.addEventListener('click', (event) => {
        if (event.target.closest('#rebuy-cart-close')) {
          setTimeout(async () => {
            await updateCartButtons('Rebuy close button');
          }, 300);
        }
      });
    }
    
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      let wasActive = cartDrawer.classList.contains('active');
      
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const isActive = cartDrawer.classList.contains('active');
            
            if (isActive !== wasActive) {
              wasActive = isActive;
              
              if (!isActive) {
                setTimeout(async () => {
                  await updateCartButtons('native cart drawer closed');
                }, 100);
              } else {
                setTimeout(async () => {
                  await updateCartButtons('native cart drawer opened');
                }, 300);
              }
            }
          }
        });
      });

      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class']
      });

      const closeButton = cartDrawer.querySelector('.drawer__close');
      if (closeButton) {
        closeButton.addEventListener('click', () => {
          setTimeout(async () => {
            await updateCartButtons('native close button');
          }, 200);
        });
      }

      const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', () => {
          setTimeout(async () => {
            await updateCartButtons('native overlay');
          }, 200);
        });
      }
    }
  };

  setTimeout(setupCartDrawerObserver, 1000);
  
  setInterval(() => {
    const rebuyCart = document.querySelector('.rebuy-cart__flyout');
    if (rebuyCart && !rebuyCart.dataset.observerSetup) {
      rebuyCart.dataset.observerSetup = 'true';
      setupCartDrawerObserver();
    }
  }, 2000);

  const setupEmptyCartFlyoutHider = () => {
    const flyout = document.querySelector('.rebuy-cart__flyout');
    if (!flyout) return;

    const applyEmptyState = () => {
      const checkoutArea = flyout.querySelector('[data-rebuy-component="checkout-area"]');
      const footer = flyout.querySelector('[data-rebuy-cart-anchor="footer"]');
      const isEmpty = checkoutArea && checkoutArea.style.display === 'none';
      if (footer) {
        if (isEmpty) {
          footer.setAttribute('data-cart-empty', 'true');
        } else {
          footer.removeAttribute('data-cart-empty');
        }
      }
    };

    new MutationObserver(applyEmptyState).observe(flyout, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      subtree: true,
      childList: true
    });

    applyEmptyState();
  };

  setTimeout(setupEmptyCartFlyoutHider, 1000);

  setInterval(() => {
    const flyout = document.querySelector('.rebuy-cart__flyout');
    if (flyout && !flyout.dataset.emptyHiderSetup) {
      flyout.dataset.emptyHiderSetup = 'true';
      setupEmptyCartFlyoutHider();
    }
  }, 2000);
});




document.addEventListener('DOMContentLoaded', () => {
  // Find all cart icons and submit buttons
  const cartIcons = document.querySelectorAll('[id^="cart-icon-bubble"]');
  const submitButtons = document.querySelectorAll('.product-form__submit');

  function updateAllCartUI(count) {
    // Get all cart count elements across the page
    const cartCountBubbles = document.querySelectorAll('.cart-count-bubble');
    
    requestAnimationFrame(() => {
      cartCountBubbles.forEach(bubble => {
        const countSpan = bubble.querySelector('span[aria-hidden="true"]');
        const ariaSpan = bubble.querySelector('.visually-hidden');

        // Update visible count
        if (countSpan) {
          countSpan.innerHTML = count;
          countSpan.setAttribute('data-count', count);
        }

        // Update aria label
        if (ariaSpan) {
          ariaSpan.innerHTML = `${count} item${count !== 1 ? 's' : ''}`;
        }
      });

      // Verify all counts match
      const allCounts = [...document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]')]
        .map(span => parseInt(span.textContent));
      
      const mismatch = allCounts.some(spanCount => spanCount !== count);
      
      if (mismatch) {
        cartCountBubbles.forEach(bubble => {
          const countSpan = bubble.querySelector('span[aria-hidden="true"]');
          if (countSpan) countSpan.innerHTML = count;
        });
      }

      
    });
  }

  // Add click handlers
  submitButtons.forEach(button => {
    button.addEventListener('click', () => {
      // Get count from first available counter
      const firstCounter = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      const currentCount = parseInt(firstCounter?.textContent || '0');
      const newCount = currentCount + 1;
      
      // Update all UI elements
      updateAllCartUI(newCount);

      // Verify updates after a frame
      requestAnimationFrame(() => {
        const counters = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
        const anyMismatch = [...counters].some(counter => 
          parseInt(counter.textContent) !== newCount
        );

        if (anyMismatch) {
          updateAllCartUI(newCount);
        }
      });
    });
  });
});
